import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText, Rocket, ShieldCheck, Bike, GaugeCircle, BikeIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Engine & Gearbox Protection
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaFileInvoice,      // For Return to Invoice
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaUserShield,
} from "react-icons/fa";
import { GiMotorcycle } from "react-icons/gi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const BMWMotorradInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is it mandatory to have insurance for a BMW bike in India?",
      answer: "Yes, a minimum of third-party insurance is legally required.",
    },
    {
      question: "What type of insurance is ideal for my BMW motorcycle?",
      answer: "A comprehensive policy with zero depreciation and RSA is highly recommended for all BMW models.",
    },
    {
      question: "How much is the insurance premium for a BMW G 310 GS?",
      answer: "Premiums generally start from ₹3,200 and can go higher depending on coverage.",
    },
    {
      question: "Are imported BMW models covered under standard insurance?",
      answer: "Yes, but ensure the insurer offers coverage for high-end or CBU-imported motorcycles.",
    },
    {
      question: "Can I get cashless repairs for BMW bikes?",
      answer: "Yes, through network garages authorized by the insurer.",
    },
    {
      question: "Does insurance cover touring accidents?",
      answer: "Yes, as long as the usage falls under personal riding and not competitive racing.",
    },
    {
      question: "Is engine protection important for BMW ADV bikes?",
      answer: "Absolutely. It covers crucial components often exposed during off-road or long-distance riding.",
    },
    {
      question: "Do I need to disclose modifications or imported accessories?",
      answer: "Yes. Failing to disclose may void your claim later.",
    },
    {
      question: "What’s the benefit of Return to Invoice Cover?",
      answer: "It helps recover the full cost of your BMW bike in case of total loss or theft.",
    },
    {
      question: "Does my BMW policy cover off-road damage?",
      answer: "Most standard policies don’t. Ask your insurer for adventure-use or off-road endorsements.",
    },
    {
      question: "Is theft covered in BMW bike insurance?",
      answer: "Yes, comprehensive insurance includes theft coverage.",
    },
    {
      question: "What happens if I skip renewing my BMW insurance?",
      answer: "You’ll lose legal coverage and the No Claim Bonus benefit.",
    },
    {
      question: "Can I buy BMW bike insurance online?",
      answer: "Yes, online purchase is quicker, often cheaper, and paperless.",
    },
    {
      question: "Will the NCB apply to high-value bikes like the R 1250 GS?",
      answer: "Yes, No Claim Bonus applies uniformly to all insured bikes, including premium ones.",
    },
    {
      question: "Do I need special insurance for superbikes like the S 1000 RR?",
      answer: "You may need higher IDV, higher liability cover, and some insurers may have special plans.",
    },
    {
      question: "Are tyres and rims covered under insurance?",
      answer: "Only if you add Tyre Protection or in case of accidental damage under comprehensive cover.",
    },
    {
      question: "Can I upgrade my insurance mid-year?",
      answer: "Generally no, but at renewal, you can add or remove riders.",
    },
    {
      question: "Will I get a renewal discount if I haven’t made a claim?",
      answer: "Yes, that’s your No Claim Bonus, which increases each year.",
    },
    {
      question: "Can I transfer my old policy to a new BMW model?",
      answer: "No, but you can transfer the NCB to your new policy.",
    },
    {
      question: "Do luxury bike insurers check riding history?",
      answer: "Yes, especially for superbike riders or frequent claimants.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Full reimbursement for parts replaced during repairs.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine & Gearbox Protection",
      description: "Essential for water ingress, oil leakage, and internal damage.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice Cover",
      description: "Get back the full invoice amount if your bike is stolen or totalled.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Roadside Assistance (RSA)",
      description: "Ideal for long-distance riders and tourers.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Includes oils, coolants, nuts, bolts, filters, and more during claims.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Helmet & Gear Protection",
      description: "Insures your riding gear and high-end helmets.",
      icon: <FaUserShield className="text-4xl text-indigo-500 mb-4" />,
    },
    {
      title: "Tyre Protection Cover",
      description: "Covers costs related to tyre and tube replacement or damage.",
      icon: <FaCarCrash className="text-4xl text-gray-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];

  const benefits = [
    {
      title: "BMW G 310 R / G 310 GS",
      desc: "Entry-level roadster and ADV for new riders.",
      icon: <GaugeCircle className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "BMW F 900 R / F 850 GS / F 750 GS",
      desc: "Mid-range tourers and ADVs.",
      icon: <BadgeCheck className="w-7 h-7 text-orange-500" />,
      gradient: "from-orange-50 to-orange-100",
    },
    {
      title: "BMW S 1000 RR / S 1000 XR",
      desc: "Track-focused supersports and performance tourers.",
      icon: <Bike className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "BMW R 1250 GS / R 1250 RT",
      desc: "High-end flagship tourers for serious globetrotters.",
      icon: <Rocket className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "BMW R 18",
      desc: "Premium heritage cruiser with signature retro styling.",
      icon: <ShieldCheck className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
  ];
const bmwinsurance = [
    {
      name: "G310 R",
      path: "/bmw-g310-r-bike-insurance", 
      image: "/assets/bmwg310r.jpeg",
    },
  {
      name: "G310 GS",
      path: "/bmw-g310-gs-bike-insurance", 
      image: "/assets/bmwg310gs.jpeg",
    },
    {
      name: "G310 RR",
      path: "/bmw-g310-rr-bike-insurance", 
      image: "/assets/bmwg310rr.jpeg",
    },
    {
      name: "F900 R",
      path: "/bmw-f900-r-bike-insurance", 
      image: "/assets/bmwgf900r.jpeg",
    },
     {
      name: "F900 XR",
      path: "/bmw-f900-xr-bike-insurance", 
      image: "/assets/bmwgf900xr.jpeg",
    },
     {
      name: "1250 GS",
      path: "/bmw-r1250-gs-bike-insurance", 
      image: "/assets/bmwr1250gs.jpeg",
    },
     {
      name: "S1000 RR",
      path: "/bmw-s1000-rr-bike-insurance", 
      image: "/assets/bmwrs1000rr.jpeg",
    },
     {
      name: "M1000 RR",
      path: "/bmw-m1000-rr-bike-insurance", 
      image: "/assets/bmwm1000rr.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>BMW Motorcycle Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore BMW motorcycle insurance options in India. Compare quotes, customize coverage, and buy online to protect your BMW G 310 R, G 310 GS, S 1000 RR, R 1250 GS, or R 18."
        />
        <meta
          name="keywords"
          content="bmw motorcycle insurance, bmw g 310 r insurance, bmw s 1000 rr insurance, bmw r 1250 gs insurance, bmw r 18 insurance, two-wheeler insurance quotes"
        />
          <link rel="canonical" href="https://thefinmaster.com/bmw-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/bmw-motorrad-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="BMW Motorcycle Insurance in India" />
        <meta property="og:description" content="Compare and buy BMW motorcycle insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/bmw-motorrad-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">BMW Motorcycle Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">BMW Bike Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                BMW motorcycles combine luxury, performance, and cutting-edge engineering for an unmatched riding experience. Whether you own an adventure-ready GS series or a sporty S 1000 RR, protecting your bike starts with the right insurance coverage. This guide walks you through everything you need to know about insuring a BMW bike, from average premiums and model insights to useful add-ons, renewal tips, and FAQs.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/bmwinsurance11.jpeg"
                alt="BMW Motorcycle"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            About BMW Bikes in India
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            BMW Motorrad, the motorcycle division of BMW Group, offers a premium lineup that spans adventure tourers, roadsters, supersport bikes, and cruisers. From urban riding to cross-country trips, BMW bikes appeal to enthusiasts looking for comfort, power, and durability.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
       <div className="p-4 sm:p-8 bg-white">
                                <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                  Choose Your Bike Brand
                                </h2>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                  {bmwinsurance.map((calc, index) => (
                                    <motion.div
                                      key={index}
                                      whileHover={{ scale: 1.04 }}
                                      initial={{ opacity: 0, y: 20 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.3, delay: index * 0.05 }}
                                    >
                                      <Link
                                        to={calc.path}
                                        className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                      >
                                        <img
                                          src={calc.image}
                                          alt={calc.name}
                                          className="w-16 h-16 object-contain mb-2"
                                        />
                                        <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                          {calc.name}
                                        </span>
                                      </Link>
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why BMW Owners Need Comprehensive Bike Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            BMW bikes are built for long rides, high speeds, and premium comfort. That makes insurance essential for financial safety and peace of mind:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Required by Indian law for legal ownership</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Covers damages from accidents, natural disasters, theft, and fire</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>High repair and replacement costs necessitate financial backup</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>Ensures protection for imported or luxury parts and accessories</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Provides third-party liability cover for on-road safety compliance</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your BMW stays protected, legally compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
          <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
             <div className="flex flex-col md:flex-row justify-between items-center gap-10">
               <div className="max-w-xl space-y-6 text-center md:text-left">
                 <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                   Calculate Your TwoWheeler Insurance <br />
                   in <span className="text-blue-600">Seconds</span>
                 </h2>
                 <p className="text-gray-700 text-lg">
                   Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
                 </p>
                 <Link
                   to="/financial-tools-calculators/bike-insurance-calculator"
                   className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
                 >
                   Start Calculating <ArrowRight className="w-5 h-5" />
                 </Link>
               </div>
               <div className="space-y-4 text-left">
                 {features.map((item, index) => (
                   <div key={index} className="flex items-center gap-3">
                     <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                     <span className="text-gray-800 font-medium">{item.text}</span>
                   </div>
                 ))}
               </div>
             </div>
           </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          BMW Bike Models & Insurance Premium Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Premiums vary based on city, IDV, rider profile, coverage selected, and add-ons.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "BMW G 310 R", category: "Entry-level Naked", idv: "₹2.60 – ₹2.80 Lakh", premium: "₹3,000 – ₹3,500" },
              { model: "BMW G 310 GS", category: "Adventure Touring", idv: "₹3.00 – ₹3.20 Lakh", premium: "₹3,200 – ₹3,800" },
              { model: "BMW F 850 GS", category: "Mid ADV Tourer", idv: "₹12.00 – ₹13.00 Lakh", premium: "₹8,000 – ₹10,000" },
              { model: "BMW S 1000 RR", category: "Supersport", idv: "₹21.00 – ₹23.00 Lakh", premium: "₹14,000 – ₹18,000" },
              { model: "BMW R 1250 GS Adventure", category: "High-End ADV", idv: "₹23.00 – ₹25.00 Lakh", premium: "₹15,000 – ₹20,000" },
              { model: "BMW R 18", category: "Cruiser", idv: "₹20.00 – ₹22.00 Lakh", premium: "₹13,000 – ₹17,000" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">BMW Model</th>
                  <th className="p-3 border border-gray-300 text-left">Type</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["BMW G 310 R", "Entry-level Naked", "₹2.60 – ₹2.80 Lakh", "₹3,000 – ₹3,500"],
                  ["BMW G 310 GS", "Adventure Touring", "₹3.00 – ₹3.20 Lakh", "₹3,200 – ₹3,800"],
                  ["BMW F 850 GS", "Mid ADV Tourer", "₹12.00 – ₹13.00 Lakh", "₹8,000 – ₹10,000"],
                  ["BMW S 1000 RR", "Supersport", "₹21.00 – ₹23.00 Lakh", "₹14,000 – ₹18,000"],
                  ["BMW R 1250 GS Adventure", "High-End ADV", "₹23.00 – ₹25.00 Lakh", "₹15,000 – ₹20,000"],
                  ["BMW R 18", "Cruiser", "₹20.00 – ₹22.00 Lakh", "₹13,000 – ₹17,000"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for BMW Bike Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Owning a premium motorcycle like a BMW means you should go beyond basic coverage. These add-ons enhance protection significantly:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurance Providers for BMW Bikes in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Choose reliable insurers that offer premium segment coverage, cashless service networks, and quick claim settlements:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Motorcycle-friendly plans and app-based claims for premium bikes.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Wide network and flexible add-ons for BMW models.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Covers motorcycle-specific damages like engine or imported parts.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">TATA AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Good for high-value BMW bikes with customizable IDV.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">Known for quick digital claims and paperless process.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🛡️</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Offers premium segment coverage for BMW Motorrad.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🔒</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Reliance General Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Provides cashless service and quick claim settlements.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Get the Best BMW Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To get the best premium rates and coverage benefits, follow these smart tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Always compare premiums and features across insurers.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Choose a policy that includes engine and component coverage.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Renew your policy before expiry to retain NCB benefits.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Declare all accessories during policy issuance.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Check if the insurer covers motorcycle-specific parts and roadside support.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required to Insure a BMW Motorcycle
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Have the following documents ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Owner’s identity proof (Aadhaar, PAN, etc.)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Passport-size photograph</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Bike invoice (for new bikes)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Previous insurance copy (if renewing)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs About BMW Motorrad Bike Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <BikeIcon />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                BMW bikes blend performance, luxury, and road presence like no other. Whether you own the compact G 310 R or the adventure-ready R 1250 GS, a robust insurance policy ensures your prized possession stays protected against every eventuality.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Use TheFinMaster to compare BMW bike insurance quotes, explore tailored plans, and lock in coverage that matches your riding lifestyle and bike’s value — all in a few simple clicks.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default BMWMotorradInsurance;